import React, { useState } from 'react';
import { Button, Checkbox } from '@cloudclevr/components';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '@store/authStore';
import { UserRoles } from '@hooks/users/types';
import { useCheckTermsConditions } from '@hooks/users';
import { useAuth } from '@auth';
import logo from '@assets/svg/logo.svg';

export const TermsAcceptancePage: React.FC = () => {
  const authStore = useAuthStore();
  const authContext = useAuth();
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const termsAndConditionsMutation = useCheckTermsConditions();

  const handleLogout = async () => {
    authStore.setIsAuthenticated(false);
    authStore.setToken(null);
    authStore.setUserRole(null);
    authStore.setIsDataFetched(false);
    authStore.setTermsAndConditions(false);
    authStore.setUser(null);

    try {
      await authContext.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleContinue = () => {
    termsAndConditionsMutation.mutateAsync().then(() => {
      // T&C accepted — store will update and BaseLayout will render dashboard
    });
  };

  return (
    <div className="min-w-screen flex min-h-screen items-center justify-center bg-white px-6 pb-0 pt-6 md:px-6 md:py-6">
      <div className="w-full overflow-hidden rounded text-gray-500 md:relative">
        {/* Logo */}
        <img
          src={logo}
          className="absolute left-6 start-4 top-[1.5rem] block h-[28px] w-[150px] transform md:left-0 md:top-0"
          alt="Clevr360 Logo"
        />

        {/* Two-panel layout */}
        <div className="mb-3 mt-[3rem] flex h-[100vh] w-full flex-col items-center justify-start gap-4 md:mb-0 md:mt-0 md:flex md:h-auto md:flex-row md:justify-evenly md:gap-0">
          {/* Left panel: T&C content */}
          <div className="md:order-0 order-1 w-full px-2 md:w-1/2 md:px-10 lg:px-[120px]">
            <div className="mb-[12px] text-left md:mb-[24px]">
              <h1 className="text-[40px] font-light leading-36px] text-interfaceColor-100 md:text-[64px] md:leading-[72px]">
                Before you go any further...
              </h1>
            </div>

            {authStore.role === UserRoles.SUPERADMIN ? (
              <div className="flex flex-col gap-5">
                <p className="text-base text-interfaceColor-80">
                  Before you get started with Clevr360, read our terms and
                  conditions. Check the box if you&apos;re happy to proceed.
                </p>

                <Button
                  testId="terms-view-button"
                  variant="outlineLight"
                  onClick={() => navigate('/terms-and-conditions')}
                  className="w-full justify-between mt-3 mb-3"
                >
                  Clevr360 terms and conditions
                </Button>

                <Checkbox
                  testId="terms-checkbox"
                  checked={acceptedTerms}
                  onChange={() => setAcceptedTerms(!acceptedTerms)}
                  name="terms"
                  label="I've read and agree to the terms and conditions."
                />

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    testId="terms-accept-button"
                    disabled={!acceptedTerms}
                    variant="primary"
                    onClick={handleContinue}
                  >
                    Accept
                  </Button>
                  <Button
                    testId="terms-logout-button"
                    type="button"
                    onClick={handleLogout}
                    variant="dark"
                  >
                    Logout
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                <p className="text-base text-interfaceColor-80">
                  Before you get started with Clevr360, your organisation needs to
                  accept our terms and conditions. The person who&apos;s in charge of
                  your Clevr360 account will do this when they log into Clevr360
                  for the first time.
                </p>

                <div>
                  <Button
                    testId="terms-logout-button"
                    type="button"
                    onClick={handleLogout}
                    variant="dark"
                  >
                    Logout
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right panel: Hello banner */}
          <div className="order-0 relative hidden w-full md:order-1 md:block md:w-1/2">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center text-center text-8xl text-white md:flex">
              <div>Hello</div>
            </div>
            <img
              src="/templates/images/login-bg.jpg"
              alt="Login banner"
              className="h-[257px] w-full rounded-[4px] object-fill md:h-[calc(100vh-3rem)] md:object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
